
import type { StatsResponse } from '@empathybroker/tsclient-stats/dist/models/stats-response'

import type { Kpi } from '../../components/kpi-counter-banner.component.vue'
import type { InsightsBarData } from '../../components/shared/epb-insights-bar/epb-insights-bar.types'
import type { ContextState, DataTableColumn, Pagination, SemanticsMessages } from '../../models'
import { FormatEnum } from '@empathybroker/tsclient-stats/dist/enums/format.enum'
import { StatsReportsIdsEnum } from '@empathybroker/tsclient-stats/dist/enums/stats-reports-ids.enum'
import { computed, defineComponent, getCurrentInstance, ref, watch } from 'vue'
import DataTable from '../../components/crud/data-table.component.vue'
import PaginationBar from '../../components/crud/pagination-bar.component.vue'
import SemanticsDistributionChart from '../../components/insights/analytics/semantics-distribution-chart.component.vue'
import InsightsNoResults from '../../components/insights/insights-no-results.component.vue'
import { UiProgressLinear } from '../../components/keen-ui'
import KpiCounterBanner from '../../components/kpi-counter-banner.component.vue'
import { EpbInsightsBar } from '../../components/shared/epb-insights-bar'
import { EpbText } from '../../components/shared/epb-text'
import EpbTabs from '../../components/tabs.component.vue'
import TransitionSize from '../../components/transition-size.component.vue'
import { mapActions, mapGetters } from '../../composables/utils'
import { AlignEnum, ColorEnum, OriginsEnum } from '../../enums'
import { ANALYTICS_CONSTS } from '../../store/analytics/analytics.consts'
import { CONTEXT_CONSTS } from '../../store/context/context.consts'
import { DownloadUtils } from '../../utils/download.utils'

interface SemanticsDataTableRow {
  query: string
  quantity: number
  click: string
  a2c: number
  checkouts: number
}

export default defineComponent({
  name: 'Semantics',
  components: {
    DataTable,
    EpbInsightsBar,
    EpbTabs,
    EpbText,
    InsightsNoResults,
    KpiCounterBanner,
    PaginationBar,
    SemanticsDistributionChart,
    TransitionSize,
    UiProgressLinear,
  },
  setup() {
    const baseClass = 'epb-semantics'

    const ctx = getCurrentInstance()
    if (!ctx) {
      throw new Error("'ctx' is not defined")
    }

    const messages: SemanticsMessages = ctx.proxy.$epb.messages.insights.semantics

    const itemsPerPageOptions = [10, 50, 100, 200]

    const pagination = ref({
      page: 1,
      itemsPerPage: itemsPerPageOptions[0],
      totalItems: 0,
    })

    const semanticsNoResult = ref()
    const semanticsLowResult = ref()

    const originsByQueryOverallDataNoResult = ref()
    const originsByQueryOverallDataLowResult = ref()

    const originsByQuery = ref()
    const originsByQueryOverall = ref()

    const originsByQueryNoResult = ref()
    const originsByQueryLowResult = ref()

    const totalOriginsByQueryNoResults = ref()
    const totalOriginsByQueryLowResults = ref()

    const loading = ref(false)
    const loadingTable = ref(false)

    const tabsOptions = [
      { id: OriginsEnum.OVERALL, text: 'Semantics overall' },
      { id: OriginsEnum.NO_RESULTS, text: 'Semantics no-results' },
      { id: OriginsEnum.LOW_RESULTS, text: 'Semantics low-results' },
    ]

    const selectedTab = ref(tabsOptions[0].id)

    const { fetchStatsReport, loadDistData } = mapActions(
      ctx.proxy.$store,
      ANALYTICS_CONSTS.moduleName,
      {
        fetchStatsReport: ANALYTICS_CONSTS.actions.fetchStatsReport,
        loadDistData: ANALYTICS_CONSTS.actions.loadDistData,
      },
    )

    const { context } = mapGetters<ContextState>(ctx.proxy.$store, CONTEXT_CONSTS.moduleName, {
      context: CONTEXT_CONSTS.getters.context,
    })

    const { analyticsData } = mapGetters(ctx.proxy.$store, ANALYTICS_CONSTS.moduleName, {
      analyticsData: ANALYTICS_CONSTS.getters.data,
    })

    const columns = computed((): Array<DataTableColumn> => {
      return [
        {
          id: 'query',
          text: ctx.proxy.$epb.utils.compileMessage(messages.semanticsQueriesTitle, {
            type: tabsOptions.find(tab => tab.id === selectedTab.value)?.text,
          }),
          width: '40%',
        },
        {
          id: 'quantity',
          text: messages.table.quantity,
          width: '15%',
          align: AlignEnum.CENTER,
        },
        { id: 'click', text: messages.table.clicks, width: '15%', align: AlignEnum.CENTER },
        { id: 'a2c', text: messages.table.add2carts, width: '15%', align: AlignEnum.CENTER },
        {
          id: 'checkouts',
          text: messages.table.checkouts,
          width: '15%',
          align: AlignEnum.CENTER,
        },
      ]
    })

    const semanticsNoResultsCount = computed((): number => {
      return semanticsNoResult.value ? semanticsNoResult.value.queryCount : 0
    })

    const semanticsQueriesCount = computed((): number => {
      return (
        (semanticsNoResult.value ? semanticsNoResult.value.queryCount : 0) +
        (semanticsLowResult.value ? semanticsLowResult.value.queryCount : 0)
      )
    })

    const kpiCount = computed(() => {
      if (
        (selectedTab.value === OriginsEnum.NO_RESULTS && semanticsNoResult.value) ||
        (selectedTab.value === OriginsEnum.OVERALL && semanticsLowResult.value === undefined)
      ) {
        return [
          semanticsNoResult.value?.queryCount ?? 0,
          semanticsNoResult.value?.clickCount ?? 0,
          semanticsNoResult.value?.add2cartCount ?? 0,
          semanticsNoResultsCount.value,
        ]
      } else if (
        (selectedTab.value === OriginsEnum.LOW_RESULTS && semanticsLowResult.value) ||
        (selectedTab.value === OriginsEnum.OVERALL && semanticsNoResult.value === undefined)
      ) {
        return [
          semanticsLowResult.value?.queryCount ?? 0,
          semanticsLowResult.value?.clickCount ?? 0,
          semanticsLowResult.value?.add2cartCount ?? 0,
          semanticsNoResultsCount.value,
        ]
      } else if (semanticsNoResult.value && semanticsLowResult.value) {
        return [
          semanticsQueriesCount.value,
          semanticsNoResult.value?.clickCount + semanticsLowResult.value?.clickCount || 0,
          semanticsNoResult.value?.add2cartCount + semanticsLowResult.value?.add2cartCount || 0,
          semanticsNoResultsCount.value,
        ]
      }
      return [0, 0, 0, 0]
    })

    const semanticQueriesTitle = computed(() => {
      const tabText = tabsOptions.find(tab => tab.id === selectedTab.value)?.text ?? ''
      return ctx.proxy.$epb.utils.compileMessage(messages.semanticsQueriesTitle, {
        type: tabText,
      })
    })

    const formatNumber = (number: number): string => {
      return ctx.proxy.$epb.utils.formatNumber(number, { maximumFractionDigits: 2 }, true)
    }

    const kpis = computed((): Kpi[] => {
      const baseKPIs = [
        {
          label: semanticQueriesTitle.value,
          icon: 'highlights',
          color: ColorEnum.PURPLE_75,
          value: formatNumber(kpiCount.value[0]),
        },
        {
          label: messages.header.clicksGained,
          icon: 'actions',
          color: ColorEnum.GREEN_75,
          value: formatNumber(kpiCount.value[1]),
        },
        {
          label: messages.header.add2cartsGained,
          icon: 'add2cart',
          color: ColorEnum.BLUE_75,
          value: formatNumber(kpiCount.value[2]),
        },
      ]
      if (selectedTab.value !== OriginsEnum.LOW_RESULTS) {
        baseKPIs.push({
          label: messages.header.noResultsDrop,
          icon: 'arrowdown',
          color: ColorEnum.RED_75,
          value: formatNumber(kpiCount.value[3]),
        })
      }

      return baseKPIs
    })

    const totalQueriesCount = computed(
      (): number => analyticsData.value.overall.dist.currentPeriod?.data.queryCount,
    )

    const insightsDataNoResults = computed((): InsightsBarData => {
      const overallNoResults =
        analyticsData.value.overall.dist.currentPeriod?.data?.noResultsCount ?? 0

      return {
        main: {
          id: 'no-results',
          message: messages.insightsBar.noResultsBar.main,
          quantity: overallNoResults + semanticsNoResultsCount.value,
        },
        secondary: [
          {
            id: 'no-results-semantics',
            message: messages.insightsBar.noResultsBar.secondary,
            quantity: semanticsNoResultsCount.value ? overallNoResults : 0,
          },
        ],
      }
    })

    const insightsDataInteractionsGainedResults = computed((): InsightsBarData => {
      const dailySearchResults = analyticsData.value.overall.dist.currentPeriod?.data

      return {
        main: {
          id: 'semantics',
          message: ctx.proxy.$epb.utils.compileMessage(messages.semanticsQueriesTitle, {
            type: tabsOptions.find(tab => tab.id === selectedTab.value)?.text,
          }),
          quantity: kpiCount.value[0],
        },
        secondary: [
          {
            id: 'clicks',
            message: messages.insightsBar.interactionsGainedBar.secondary.clicks,
            quantity: kpiCount.value[1],
            previousQuantity: dailySearchResults?.clickCount,
          },
          {
            id: 'add2cart',
            message: messages.insightsBar.interactionsGainedBar.secondary.add2cart,
            quantity: kpiCount.value[2],
            previousQuantity: dailySearchResults?.add2cartCount,
          },
        ],
      }
    })

    const fetchData = async function (originIds: string[]): Promise<StatsResponse> {
      const originsResponse: StatsResponse = await fetchStatsReport({
        id: StatsReportsIdsEnum.ORIGINS,
        params: {},
      })

      return originsResponse.data.origins
        .filter(origin => originIds.includes(origin.id))
        .reduce((acc, origin) => {
          Object.entries(origin).forEach(([key, value]) => {
            if (key === 'id') return
            acc[key] = (acc[key] || 0) + value
          })
          return acc
        }, {})
    }

    const getPaginationData = function (paginationParam: Pagination): {
      offset: number
      rows: number
    } {
      return {
        offset: (pagination.value.page - 1) * pagination.value.itemsPerPage,
        rows: paginationParam.itemsPerPage,
      }
    }

    const fetchOriginsByQueryData = async function (
      origins: string[],
      download = false,
      paginationParams = pagination.value,
    ): Promise<StatsResponse> {
      const paginationData = getPaginationData(paginationParams)

      const originsByQueryResponse: StatsResponse = await fetchStatsReport({
        id: StatsReportsIdsEnum.ORIGINS_BY_QUERY,
        params: {
          ...paginationData,
          origins,
          format: download ? FormatEnum.CSV : FormatEnum.JSON,
        },
      })

      return originsByQueryResponse.data
    }

    const fetchOriginsByQueryPaginationData = async function (
      origins: string[],
    ): Promise<StatsResponse> {
      const originsByQueryPaginationResponse: StatsResponse = await fetchStatsReport({
        id: StatsReportsIdsEnum.ORIGINS_BY_QUERY_PAGINATION,
        params: {
          origins,
        },
      })

      return originsByQueryPaginationResponse.data.itemsCount ?? 0
    }

    const tableData = computed((): SemanticsDataTableRow[] => {
      let data
      if (selectedTab.value === OriginsEnum.OVERALL && originsByQuery.value) {
        data = originsByQuery.value
      } else if (
        (selectedTab.value === OriginsEnum.OVERALL && originsByQueryNoResult.value) ||
        selectedTab.value === OriginsEnum.NO_RESULTS
      ) {
        data = originsByQueryNoResult.value
      } else if (
        (selectedTab.value === OriginsEnum.OVERALL && originsByQueryLowResult.value) ||
        selectedTab.value === OriginsEnum.LOW_RESULTS
      ) {
        data = originsByQueryLowResult.value
      } else {
        data = []
      }

      if (!data) {
        return []
      } else {
        return data.map((item: any) => ({
          query: item.query,
          quantity: item.queryCount,
          click: item.clickCount,
          a2c: item.add2cartCount,
          checkouts: item.checkoutCount,
        }))
      }
    })

    const paginationTotalItems = computed(() => {
      if (selectedTab.value === OriginsEnum.NO_RESULTS) {
        return totalOriginsByQueryNoResults.value
      } else if (selectedTab.value === OriginsEnum.LOW_RESULTS) {
        return totalOriginsByQueryLowResults.value
      } else {
        return totalOriginsByQueryNoResults.value + totalOriginsByQueryLowResults.value
      }
    })

    const loadTableData = async function (): Promise<void> {
      try {
        loadingTable.value = true

        if (selectedTab.value === OriginsEnum.NO_RESULTS) {
          originsByQueryOverallDataNoResult.value = await fetchOriginsByQueryData([
            'semantics:no_results',
            'overview:no_results',
            'ai_carousel:no_results',
          ])

          originsByQueryNoResult.value = originsByQueryOverallDataNoResult.value.dist
        } else if (selectedTab.value === OriginsEnum.LOW_RESULTS) {
          originsByQueryOverallDataLowResult.value = await fetchOriginsByQueryData([
            'semantics:low_results',
            'overview:low_results',
            'ai_carousel:low_results',
          ])

          originsByQueryLowResult.value = originsByQueryOverallDataLowResult.value.dist
        } else {
          originsByQueryOverall.value = await fetchOriginsByQueryData([
            'semantics:no_results',
            'semantics:low_results',
            'overview:no_results',
            'overview:low_results',
            'ai_carousel:no_results',
            'ai_carousel:low_results',
          ])

          originsByQuery.value = originsByQueryOverall.value.dist
        }
      } catch (error) {
        ctx.proxy.$emit('showError', ctx.proxy.$epb.utils.decodeErrorMessage(error))
      } finally {
        loadingTable.value = false
      }
    }

    const loadData = async function (): Promise<void> {
      loading.value = true
      try {
        semanticsNoResult.value = await fetchData([
          'semantics:no_results',
          'overview:no_results',
          'ai_carousel:no_results',
        ])
        semanticsLowResult.value = await fetchData([
          'semantics:low_results',
          'overview:low_results',
          'ai_carousel:low_results',
        ])

        await loadTableData()

        totalOriginsByQueryNoResults.value = await fetchOriginsByQueryPaginationData([
          'semantics:no_results,overview:no_results,ai_carousel:no_results',
        ])
        totalOriginsByQueryLowResults.value = await fetchOriginsByQueryPaginationData([
          'semantics:low_results,overview:low_results,ai_carousel:low_results',
        ])

        analyticsData.value.overall.dist.currentPeriod = undefined

        pagination.value.totalItems = paginationTotalItems.value

        await loadDistData()
      } catch (error) {
        semanticsNoResult.value = []
        semanticsLowResult.value = []
        originsByQueryOverallDataLowResult.value = []
        originsByQueryOverallDataNoResult.value = []
        originsByQueryNoResult.value = []
        originsByQueryLowResult.value = []
        totalOriginsByQueryNoResults.value = 0
        totalOriginsByQueryLowResults.value = 0
        ctx.proxy.$emit('showError', ctx.proxy.$epb.utils.decodeErrorMessage(error))
      } finally {
        loading.value = false
      }
    }

    const onPaginationChange = (paginationParam: Pagination): void => {
      pagination.value = paginationParam
      loadTableData()
    }

    const onDownload = async (paginationParam: Pagination): Promise<void> => {
      const filter =
        selectedTab.value === OriginsEnum.OVERALL
          ? [
              'semantics:no_results',
              'semantics:low_results',
              'overview:no_results',
              'overview:low_results',
              'ai_carousel:no_results',
              'ai_carousel:low_results',
            ]
          : selectedTab.value === OriginsEnum.LOW_RESULTS
            ? ['semantics:low_results', 'overview:low_results', 'ai_carousel:low_results']
            : ['semantics:no_results', 'overview:no_results', 'ai_carousel:no_results']
      const downloadData = await fetchOriginsByQueryData(filter, true, paginationParam).then(
        response => response.toString(),
      )

      DownloadUtils.downloadCSV(downloadData, messages.downloadFile)
    }

    watch(
      () => context.value.filters,
      () => {
        pagination.value.totalItems = paginationTotalItems.value
        pagination.value.page = 1
        loadData()
      },
      { immediate: true },
    )

    watch(
      () => selectedTab.value,
      () => {
        pagination.value.totalItems = paginationTotalItems.value
        pagination.value.page = 1
        loadTableData()
      },
      { immediate: true },
    )

    return {
      baseClass,
      messages,
      pagination,
      itemsPerPageOptions,
      analyticsData,
      columns,
      kpiCount,
      kpis,
      loading,
      loadingTable,
      selectedTab,
      tableData,
      tabsOptions,
      OriginsEnum,
      insightsDataNoResults,
      insightsDataInteractionsGainedResults,
      totalQueriesCount,
      formatNumber,
      onPaginationChange,
      onDownload,
    }
  },
})
